/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

// TB-071: must stay the first import (storage fallback before anything reads storage)
import './utils/storageFallback.js'
import { createApp } from 'vue'
import App from './App.vue'
import { initTheme } from './theme/theme.js'
import {
  attachForegroundMessageLogger,
  manageNotifications
} from './firebase.js'

// Vuesax Component Framework
// import Vuesax from 'vuesax' // REMOVED: Not compatible with Vue 3
import 'material-icons/iconfont/material-icons.css' //Material Icons
// import 'vuesax/dist/vuesax.css' // REMOVED: Not compatible with Vue 3
// Vue.use(Vuesax)

import Vue3Signature from 'vue3-signature'
// import VeeValidate from 'vee-validate'
// Vue.use(VeeValidate)
import commonMixin from './mixin/commonMixin.js'

// axios
import axios from './axios.js'
import { loadDisplayCurrency } from './utils/displayCurrency' // TB-122
import { loadVisitTypes } from './utils/visitTypes' // TB-184
import { exitEstDoctorView, shouldExitEstDoctorView } from './utils/estDoctorSelection' // TB-081 (DEF-218)
// Vue.prototype.$http = axios

// Filters
// import './filters/filters.js'

// Theme Configurations
import '../themeConfig.js'

// Globally Registered Components
import { registerGlobalComponents } from './globalComponents.js'

// Styles: SCSS
import './assets/scss/main.scss'

// Tailwind
import '@/assets/css/main.css'

import 'v-calendar/style.css'

// Vue Router
import router from './router'

// Vuex Store
import store from './store/store'

import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

// Vuejs - Vue wrapper for hammerjs
// import { VueHammer } from 'vue2-hammer' // REMOVED: Not compatible with Vue 3

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
// Google Maps
import VueGoogleMaps from '@fawmi/vue-google-maps'
import { MAPS_KEY, MAPS_REGION, mapsLanguage } from './config/map'

// PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar'
import 'vue3-perfect-scrollbar/style.css'

import vueCountryRegionSelect from 'vue3-country-region-select' // Check for Vue 3 compatible version

// Feather font icon
require('./assets/css/iconfont.css')

import VueTelInput from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
const globalOptions = {
  mode: 'international',
  defaultCountry: 'BH',
  onlyCountries: ['BH'],
  dropdownOptions: {
    disabled: true
  }
}
import VueFeather from 'vue-feather'
import 'vue-toast-notification/dist/theme-sugar.css'
import VueToast from 'vue-toast-notification'
import { wrapToast } from './utils/toast'
import * as Sentry from '@sentry/vue'
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(App)
// Minimal $vs polyfill for RTL and other references
app.config.globalProperties.$vs = { rtl: false }
app.use(VueTelInput, globalOptions)
app.use(vSelect)
app.use(router)
app.use(PerfectScrollbarPlugin)
app.use(Vue3Signature)
app.use(pinia)
app.use(vueCountryRegionSelect)
app.use(store)
app.component('FeatherIcon', VueFeather)
// Apply the mixin globally
app.mixin(commonMixin)
// If you need to provide axios globally:
app.config.globalProperties.$http = axios
app.config.globalProperties.ApiBaseURLUser =
  process.env.VUE_APP_API_BASE_URL_USER
registerGlobalComponents(app)

// Provide a default pagination function and perpage value
app.provide('pagination', (total) => {
  const perpage = 10
  const pages = Math.ceil(total / perpage)
  return Array.from({ length: pages }, (_, i) => i + 1)
})
app.provide('perpage', 10)

// TB-063: key from the build env (VUE_APP_GOOGLE_MAPS_KEY), Bahrain region bias.
app.use(VueGoogleMaps, {
  load: {
    key: MAPS_KEY,
    libraries: 'places',
    region: MAPS_REGION,
    language: mapsLanguage()
  }
})

// TB-057: drop URL fragments (consent tokens) from anything sent to Sentry
// TB-081 (DEF-174): also blank token= query values (set-password link fallback)
const stripToken = (s) => s.replace(/([?&]token=)[^&#\s]*/gi, '$1[Filtered]')
const stripHash = (url) => (typeof url === 'string' ? stripToken(url.split('#')[0]) : url)
const scrubCrumb = (crumb) => {
  if (crumb && crumb.data) {
    ['from', 'to', 'url'].forEach((k) => { crumb.data[k] = stripHash(crumb.data[k]) })
  }
  if (crumb && typeof crumb.message === 'string') crumb.message = stripToken(crumb.message.replace(/#\S*/g, ''))
  return crumb
}
const scrubEvent = (event) => {
  if (event && event.request && event.request.url) event.request.url = stripHash(event.request.url)
  if (event && Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubCrumb)
  if (event && typeof event.transaction === 'string') event.transaction = stripHash(event.transaction)
  return event
}

Sentry.init({
  app,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // TB-057: the consent link carries its token in the fragment (#t=...); never send it
  beforeBreadcrumb: (crumb) => scrubCrumb(crumb),
  beforeSend: (event) => scrubEvent(event)
});

app.use(VueToast, {
  position: 'top-right'
})
// TB-080 (DEF-170): this.$toast escapes HTML and skips empty messages.
app.config.globalProperties.$toast = wrapToast(app.config.globalProperties.$toast)

router.beforeEach((to, from, next) => {
  window.document.title =
    to.meta && to.meta.title ? 'Tabibbi | ' + to.meta.title : 'Tabibbi'
  // TB-057: public pages (patient consent link) open for everyone, whatever the session role
  if (to.meta && to.meta.public) {
    // TB-057: no session recording on the consent page (token in the URL, health data on screen)
    if (to.path.startsWith('/consent') && typeof window.clarity === 'function') window.clarity('stop')
    // TB-081 (DEF-174): nor on the invited doctor's set-password page (token in the link)
    if ((to.name === 'doctor-set-password' || to.name === 'set-password') && typeof window.clarity === 'function') window.clarity('stop')
    next()
    return
  }
  var user = localStorage.getItem('user')
  let role = ''
  if (user) {
    var isLoggedIn = localStorage.getItem('_isLoggedIn')
    const userData = JSON.parse(user)
    store.dispatch('updateUserInfo', userData)
    role = userData.userRole
  }
  // Centralize protected doctor routes
  const doctorProtected = new Set([
    'doctor-invoice',
    'doctor-invoices',
    'doctor-calendar-view',
    'doctor-subscriptions',
    'doctor-stats',
    'doctor-payouts',
    'doctor-change-password',
    'doctor-poc',
    'doctor-my-earnings',
    'doctor-chat',
    'doctor-add-doctor',
    'doctor-consultation-details',
    'doctor-consultation',
    'doctor-patients-prescription-details',
    'doctor-patients-previuos-details',
    'doctor-patient-profile',
    'doctor-patients',
    'doctor-book-appoinment',
    'doctor-create-slot',
    'doctor-home',
    'doctor-calendar', // TB-073: month calendars moved off the doctor home
    'doctor-schedule', // TB-109 (A)
    'doctor-notifications',
    'doctor-help-contact',
    'doctor-notifications-setting',
    'doctor-invitations', // TB-081
    'doctor-clinics', // TB-131
    'doctor-payment-card',
    'doctor-insights', // TB-119
    'doctor-notification-preferences' // TB-118
  ])
  const toName = typeof to?.name === 'string' ? to.name : ''
  const isDoctorRoute = toName.startsWith('doctor') || (to?.path || '').startsWith('/doctor')
  if (isDoctorRoute) {
    // TB-081 (DEF-218): opening the clinic dashboard ends "viewing a doctor",
    // before the shell re-reads the role on this navigation.
    if (shouldExitEstDoctorView(toName, role)) exitEstDoctorView()
    if (role == 'provider' || role == 'establishment') {
      if (isLoggedIn) {
        next()
      } else if (doctorProtected.has(toName)) {
        next('/doctor/login')
      } else {
        next()
      }
    } else if (role == 'patient') {
      router.push('/')
    } else {
      if (isLoggedIn) {
        next()
      } else if (doctorProtected.has(toName)) {
        next('/doctor/login')
      } else {
        next()
      }
    }
  } else {
    if (role === 'provider' || role === 'establishment') {
      // Mapping of patient routes to doctor routes for logged-in providers/establishments
      const doctorRouteMap = {
        'about': '/doctor/about',
        'FAQs': '/doctor/faqs',
        'PrivacyPolicy': '/doctor/privacy-policy',
        'TermsAndConditions': '/doctor/termsandconditions',
        'RefundAndReturnPolicy': '/doctor/refundandreturnpolicy',
        'contact': '/doctor/help-contact-us'
      }

      if (doctorRouteMap[to.name]) {
        next(doctorRouteMap[to.name])
      } else {
        const homePath =
          role === 'establishment' ? '/doctor/establishment-doctor' : '/doctor'
        next(homePath)
      }
    } else {
      if (isLoggedIn) {
        next()
      } else if (
        to.name === 'loved-ones' ||
        to.name === 'select-card' ||
        to.name === 'appointments' ||
        to.name === 'appointment-details' ||
        to.name === 'profile' ||
        to.name === 'edit-profile'
      ) {
        next('/login')
      } else {
        next()
      }
    }
  }
}
)

initTheme()
loadDisplayCurrency()
loadVisitTypes()
app.mount('#app')

// Initialize FCM foreground listener and ensure token retrieval on app start
try {
  attachForegroundMessageLogger()
  manageNotifications({ prompt: false }).catch(() => { })
} catch (_) { }

// Handle deep links from notification clicks (e.g., /?action=call&consultationId=123)
// try {
//   const params = new URLSearchParams(window.location.search);
//   const action = params.get('action');
//   const consultationId = params.get('consultationId');
//   if (action === 'call' && consultationId) {
//     // Optionally auto-accept call and route
//     const token = localStorage.getItem('accessToken');
//     if (token) {
//       fetch(`${process.env.VUE_APP_TRANSACTIONS_SERVICE_API}video-consultations/${consultationId}/accept`, {
//         method: 'PATCH',
//         headers: {
//           'Authorization': `Bearer ${token}`
//         }
//       }).then(async (res) => {
//         if (!res.ok) return;
//         const json = await res.json();
//         const session = json?.data;
//         if (session) {
//           session['videoConsultationId'] = consultationId;
//           localStorage.setItem('_openTokSession', JSON.stringify(session));
//           router.push('/call');
//         }
//       }).catch(() => {});
//     } else {
//       // If not authenticated, just route user to app home; backend can re-trigger
//       router.push('/');
//     }
//   }
// } catch (_) {}
